/**
 * Roles API service for Whakahaere Safety Management System
 * Uses /roles/ endpoint (ADR-048)
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export type GroupStatus = 'all' | 'active' | 'system';

export interface Group {
  id: number;
  name: string; // Internal name (may have org prefix)
  display_name: string; // User-friendly name
  is_system_role: boolean;
  organization_id: number | null;
  organization_name: string | null;
  description: string;
  permissions: number[]; // Permission IDs (for create/update)
  permission_codenames: string[]; // Permission codenames (read-only)
  user_count: number;
  cloned_from_id?: number | null;
  created_at?: string;
  updated_at?: string;
  // Legacy fields for compatibility
  organization?: string;
  members_count?: number;
  permissions_count?: number;
  status?: string;
}

export interface GroupListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Group[];
}

export interface GroupFilters {
  search?: string;
  status?: GroupStatus;
  page?: number;
  page_size?: number;
  limit?: number;
  offset?: number;
  ordering?: string;
  /**
   * Recipient-picker opt-in. When `true`, the backend
   * (`RoleFilter.filter_exclude_hidden`) force-excludes roles flagged
   * `role_metadata.is_internal=True` or
   * `role_metadata.is_vendor_managed=True`, regardless of
   * whether the caller holds `accounts.view_all_roles`.
   *
   * Pass `true` from any picker that lists "who gets notified" — the
   * Default Recipients tab, the per-protocol drawer's recipient table,
   * etc. Leave unset (or `false`) on role-management screens.
   */
  exclude_hidden?: boolean;
}

export interface GroupCreatePayload {
  name: string;
  organization_id: number;
  description?: string;
  permissions?: number[];
}

export interface GroupUpdatePayload {
  name?: string;
  description?: string;
  permissions?: number[];
}

export interface CloneRolePayload {
  name: string;
  organization_id: number;
  description?: string;
}

/**
 * Get all available roles
 */
export async function getGroups(
  filters: GroupFilters = {},
): Promise<GroupListResponse> {
  const result = await client.GET('/api/v2/roles/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as GroupListResponse;
}

/**
 * Get a single role by ID
 */
export async function getGroupById(id: number): Promise<Group> {
  const result = await client.GET('/api/v2/roles/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Group;
}

/**
 * Create a new role
 */
export async function createGroup(data: GroupCreatePayload): Promise<Group> {
  const result = await client.POST('/api/v2/roles/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Group;
}

/**
 * Update an existing role
 */
export async function updateGroup(
  id: number,
  data: GroupUpdatePayload,
): Promise<Group> {
  const result = await client.PATCH('/api/v2/roles/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Group;
}

/**
 * Delete a role
 */
export async function deleteGroup(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/roles/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

/**
 * Clone an existing role
 */
export async function cloneGroup(
  id: number,
  data: CloneRolePayload,
): Promise<Group> {
  const result = await client.POST('/api/v2/roles/{id}/clone/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Group;
}
